import { MaximizeIcon } from "lucide-react";
import { FC, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useGetSummary } from "~/api/chat/chat";
import { ContentCopyButton } from "~/components/ContentCopyButton";
import { FancyLoader } from "~/components/FancyLoader";
import { Loader } from "~/components/Loader";
import { MarkdownExportButton } from "~/components/MarkdownExportButton";
import { TabControlBar } from "~/components/TabControlBar";
import { TTSButton } from "~/components/TTSButton";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { useParentCase } from "~/features/case/hooks/useParentCase";
import { formatCaseInfo } from "~/features/chat/helpers";
import { useSummaryContent } from "~/features/chat/hooks/useSummaryContent";
import { useStoredSummary } from "~/features/chat/hooks/useSummaryStore";
import { PageAnnotationsList } from "~/features/pageAnnotations/components/PageAnnotationsList";
import { usePageAnnotations } from "~/features/pageAnnotations/hooks/usePageAnnotations";
import type { PageAnnotation } from "~/features/pageAnnotations/types";
import { useSkyViewStore } from "~/features/skyView/store";
import { useChatDocumentParams } from "~/helpers";
import { useMobile } from "~/hooks/use-mobile";
import { useDocumentSearchParams } from "~/hooks/useDocumentSearchParams";
import { cn } from "~/lib/utils";
import { trackEvent } from "~/utils/analytics";
import { shortRelativeTime } from "~/utils/date";
import { htmlToMarkdown } from "~/utils/htmlToMarkdown";
import { useNote } from "../hooks/useNote";
import { AudioRecordingButton } from "./AudioRecordingButton";
import { NoteEditor } from "./NoteEditor";

interface NotesAreaProps {
  className?: string;
  isActiveTab?: boolean;
}

export const NotesArea: FC<NotesAreaProps> = ({
  className,
  isActiveTab: _isActiveTab = true,
}) => {
  const [maximizeModalOpen, setMaximizeModalOpen] = useState(false);
  const [isReading, setIsReading] = useState(false);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const [activeLen, setActiveLen] = useState<number | null>(null);
  const [lastSpokenText, setLastSpokenText] = useState<string>("");
  const [spokenBeforeImprovement, setSpokenBeforeImprovement] =
    useState<string>("");
  const { chatId, organizationId, caseId, isViewOnly } =
    useChatDocumentParams();
  const { isMobile } = useMobile();

  const { project } = useParentCase();
  const caseInfo = project ? formatCaseInfo(project) : undefined;

  const { annotations } = usePageAnnotations(organizationId, project?.id);
  const activeAnnotationId = useSkyViewStore(
    (state) => state.activeAnnotationId
  );
  const setActiveAnnotationId = useSkyViewStore(
    (state) => state.setActiveAnnotationId
  );

  const [, updateSearchParams] = useDocumentSearchParams();

  const handleAnnotationClick = (annotation: PageAnnotation) => {
    updateSearchParams({ pageId: annotation.pageId });
    setActiveAnnotationId(annotation.id);
  };

  const summaryData = useStoredSummary(chatId, "summary");
  const { data: fetchedSummary } = useGetSummary(
    organizationId,
    caseId,
    chatId,
    {
      type: "summary",
    },
    {
      query: {
        staleTime: Infinity,
        enabled: !summaryData?.content,
      },
    }
  );

  const { data: summaryContent } = useSummaryContent(
    fetchedSummary?.contentUrl
  );

  const [isRecordingActive, setIsRecordingActive] = useState(false);
  const intl = useIntl();
  const { formatMessage } = intl;
  const {
    note,
    noteContent,
    isFetching,
    isSaving,
    isProcessingImprovement,
    isProcessingQueue,
    handleContentChange,
    handleAutoImproveChunk,
    handleNoteImprove,
  } = useNote();

  const exportFileName = `Case Notes ${formatMessage({ id: "common.case" })}`;

  const onAutoImproveChunk = async (chunk: string) => {
    if (!spokenBeforeImprovement.length) {
      setSpokenBeforeImprovement(chunk);
    }
    setLastSpokenText("");

    const improvedNote = await handleAutoImproveChunk(chunk);

    setSpokenBeforeImprovement("");

    if (improvedNote) {
      await handleContentChange(improvedNote);
    }
  };

  const onSpeechUpdate = (text: string) => {
    setLastSpokenText(text);
  };

  const isContentEmpty =
    !noteContent ||
    noteContent.trim() === "" ||
    htmlToMarkdown(noteContent).trim() === "";

  const onNoteImprove = (): Promise<void> => {
    const fullSummaryContent = summaryData?.content || summaryContent || "";
    setSpokenBeforeImprovement("");
    setLastSpokenText("");
    return handleNoteImprove(fullSummaryContent);
  };

  return (
    <>
      <div
        className={cn(
          "relative flex flex-col overflow-hidden",
          isMobile ? "h-docsidebar-content-mobile" : "h-[calc(100vh-60px)]",
          className
        )}
      >
        {annotations.length > 0 && (
          <PageAnnotationsList
            annotations={annotations}
            onAnnotationClick={handleAnnotationClick}
            activeAnnotationId={activeAnnotationId}
          />
        )}
        <div className={cn("border-zinc-100 flex-1 min-h-0")}>
          {isFetching && (
            <div className="flex items-center justify-center h-full">
              <Loader />
            </div>
          )}
          {isProcessingImprovement && (
            <div className="flex items-center justify-center absolute left-1/2 bottom-1/2 -translate-x-1/2 -translate-y-1/2">
              <FancyLoader />
            </div>
          )}
          {!isFetching && !isProcessingImprovement && !maximizeModalOpen && (
            <NoteEditor
              content={noteContent + spokenBeforeImprovement + lastSpokenText}
              onChange={handleContentChange}
              readOnly={isRecordingActive || isViewOnly}
              placeholder={formatMessage({
                id: "notes.placeholder",
                defaultMessage:
                  "Leave helpful notes here, they are visible org-wide.",
              })}
              className="h-full min-h-[400px] overflow-y-auto overflow-x-hidden"
              activeTtsIdx={isReading ? activeIdx : null}
              activeTtsLen={isReading ? activeLen : null}
              enableImages={!isViewOnly}
              showToolbar={!isViewOnly}
            />
          )}
        </div>

        {note && (
          <div
            className={`w-full h-6 px-6 items-center flex border-t border-border bg-background justify-center ${isMobile ? "flex-shrink-0" : ""}`}
          >
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              {isSaving ? (
                <span>Saving...</span>
              ) : (
                <>
                  {(() => {
                    const timeAgo = shortRelativeTime(
                      note.updatedAt,
                      intl.locale
                    );
                    const isJustNow =
                      timeAgo === "just now" || timeAgo === "à l'instant";
                    return isJustNow ? (
                      <FormattedMessage
                        id="notes.status.editedJustNowBy"
                        defaultMessage="Edited just now by"
                      />
                    ) : (
                      <FormattedMessage
                        id="notes.status.editedAgoBy"
                        defaultMessage="Edited {timeAgo} ago by"
                        values={{ timeAgo }}
                      />
                    );
                  })()}
                  <span> {note.author.split("@")[0]}</span>
                </>
              )}
            </div>
          </div>
        )}
        {!isViewOnly && (
          <AudioRecordingButton
            onRecordingStateChange={setIsRecordingActive}
            isProcessingImprovement={isProcessingImprovement}
            isLoading={isSaving || isFetching}
            onAutoImproveChunk={onAutoImproveChunk}
            onRecordingStop={onNoteImprove}
            isProcessingQueue={isProcessingQueue}
            onTextSpeechUpdate={onSpeechUpdate}
            className={`${!note && !isFetching ? "bottom-[42px]" : "bottom-[65px]"}`}
          />
        )}
        <TabControlBar className={isMobile ? "flex-shrink-0" : ""}>
          <TabControlBar.Group>
            <TTSButton
              text={htmlToMarkdown(noteContent)}
              isLoading={isSaving || isFetching}
              onStart={() => setIsReading(true)}
              onEnd={() => {
                setIsReading(false);
                setActiveIdx(null);
                setActiveLen(null);
              }}
              onBoundary={({ charIndex, charLength }) => {
                setActiveIdx(charIndex);
                setActiveLen(typeof charLength === "number" ? charLength : 0);
              }}
            />
            <Button
              variant="ghost"
              size="mini"
              disabled={isContentEmpty}
              onClick={() => {
                trackEvent({ eventName: "app/notes/maximize.button/click" });
                setMaximizeModalOpen(true);
              }}
            >
              <MaximizeIcon className="w-4 h-4 mr-1" />
              <FormattedMessage
                id="common.maximize"
                defaultMessage="Maximize"
              />
            </Button>
            <ContentCopyButton
              content={noteContent}
              size="mini"
              preCopyMessageId="copy"
              eventName="app/notes/copy_all.button/click"
              transformContent={htmlToMarkdown}
              disabled={isContentEmpty}
            />
            <MarkdownExportButton
              content={noteContent}
              fileName={exportFileName}
              buttonSize="mini"
              openEventName="app/notes/export.button/click"
              exportEventName="app/notes/export_menu_open/export_action.link/click"
              transformContent={htmlToMarkdown}
              disabled={isContentEmpty}
              title={formatMessage({
                id: "common.notes",
              })}
              caseInfo={caseInfo}
            />
          </TabControlBar.Group>
        </TabControlBar>
      </div>

      {maximizeModalOpen && (
        <Dialog open={maximizeModalOpen} onOpenChange={setMaximizeModalOpen}>
          <DialogContent className="py-0 px-0 gap-0 flex h-[calc(100dvh-32px)] max-h-[calc(100dvh-32px)] w-full max-w-2xl flex-col">
            <div className="flex flex-1 overflow-hidden flex-col">
              <div className="pt-6 px-6">
                <DialogHeader className="mb-2">
                  <DialogTitle>
                    <FormattedMessage
                      id="notes.maximize.title"
                      defaultMessage="Case Notes"
                    />
                  </DialogTitle>
                  <DialogDescription className="sr-only">
                    <FormattedMessage
                      id="notes.maximize.description"
                      defaultMessage="Maximized view of Case Notes"
                    />
                  </DialogDescription>
                </DialogHeader>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-6 pb-2">
                <NoteEditor
                  content={
                    noteContent + spokenBeforeImprovement + lastSpokenText
                  }
                  onChange={handleContentChange}
                  readOnly={isRecordingActive || isViewOnly}
                  placeholder={formatMessage({
                    id: "notes.placeholder",
                    defaultMessage:
                      "Leave helpful notes here, they are visible org-wide.",
                  })}
                  className="h-full min-h-[400px]"
                  enableImages={!isViewOnly}
                  showToolbar={!isViewOnly}
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-2 px-4 sm:px-6 py-3 border-t border-border flex-shrink-0">
              <div className="flex flex-wrap gap-2">
                <ContentCopyButton
                  content={noteContent}
                  size="sm"
                  preCopyMessageId="copyAll"
                  eventName="app/notes_maximize_modal/copy_all.button/click"
                  transformContent={htmlToMarkdown}
                  disabled={isContentEmpty}
                />
                <MarkdownExportButton
                  content={noteContent}
                  fileName={exportFileName}
                  openEventName="app/notes_maximize_modal/export.button/click"
                  exportEventName="app/notes_maximize_modal/export_menu_open/export_action.link/click"
                  transformContent={htmlToMarkdown}
                  disabled={isContentEmpty}
                  title={formatMessage({
                    id: "common.notes",
                  })}
                  caseInfo={caseInfo}
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setMaximizeModalOpen(false)}
              >
                <FormattedMessage id="close" defaultMessage="Close" />
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
